import { Fragment, useEffect, useId, useRef, useState } from 'react';

import type {
  AdminCategoryAudienceImpact,
  AdminCategoryItem,
  LineClassificationDto,
  UpdateAdminCategoryRequest,
} from '@time-tracking/shared';

import {
  useAdminCategories,
  useCategoryAudienceImpact,
  useCreateCategory,
  useUpdateCategory,
} from '../../../api/admin.js';
import {
  useClassifications,
  useCreateClassification,
  useUpdateClassification,
} from '../../../api/projects.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';
import { ConfirmDialog } from '../../projects/dialogs/ConfirmDialog.js';
import { ToastRegion } from '../../../components/ToastRegion.js';
import { useToasts, type ToastKind } from '../../../components/useToasts.js';
import { categoryTypeLabel } from '../../../utils/categoryType.js';

import '../categories-classifications.css';

// ── Badges (shape + text, never colour alone) ────────────────────────────────

function Dot({ filled }: { filled: boolean }) {
  return <span aria-hidden="true" className={`cc-dot${filled ? ' filled' : ''}`} />;
}

function TypeBadge({ isWorkTime }: { isWorkTime: boolean }) {
  return (
    <span className={`cc-badge ${isWorkTime ? 'cc-badge--work' : 'cc-badge--nonwork'}`}>
      <Dot filled={isWorkTime} />
      {categoryTypeLabel(isWorkTime)}
    </span>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span className={`cc-badge ${active ? 'cc-badge--active' : 'cc-badge--inactive'}`}>
      <Dot filled={active} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

/**
 * Marks the classification a new task starts on.
 *
 * The word "Default" is the indicator, not the colour — colour is never the sole
 * carrier of state (WCAG 1.4.1), and this pill sits next to a green/amber
 * StatusBadge where a third colour alone would say nothing. Bluebonnet wash
 * because it reads as informational; a Burnt Orange tint would be off-brand as a
 * static surface (see the note on --ut-burnt-orange-pulse in tokens.css).
 */
function DefaultBadge() {
  return <span className="cc-badge cc-badge--default">Default</span>;
}

// ── "Visible to" ─────────────────────────────────────────────────────────────

function GlobeGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function ShieldGlyph({ size, strokeWidth = 2 }: { size: number; strokeWidth?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

/**
 * The tooltip on the pill, and the one place on this row the accurate rule is
 * stated in full.
 *
 * NOT the handoff's "Hidden from contributors — appears only on approvers'
 * timesheets", on two counts. It says "hidden", which invites an administrator
 * to read this as a privacy control and eventually park something sensitive
 * behind it — and it is not one: the category catalog is ungated (a retained
 * line needs its label), so contributors can still enumerate the name, and the
 * hours reach shared analytics unchanged. And it omits administrators, who hold
 * these rows too. The visible label keeps the prototype's short "Approvers
 * only" for fidelity; the longer rule lives here and in the form hint.
 */
const APPROVER_ONLY_TOOLTIP = 'Seeded only onto approvers’ and administrators’ timesheets';

/**
 * The third column. "Everyone" is the default and reads as plain text so it
 * does not compete with the exception; "Approvers only" is a pill. Both carry a
 * word AND a shape, so neither depends on colour (WCAG 1.4.1).
 */
function VisibleToCell({ approverOnly }: { approverOnly: boolean }) {
  if (!approverOnly) {
    return (
      <span className="cc-visible-everyone">
        <GlobeGlyph size={13} />
        Everyone
      </span>
    );
  }
  return (
    <span className="cc-badge cc-badge--approvers-only" title={APPROVER_ONLY_TOOLTIP}>
      <ShieldGlyph size={11} strokeWidth={2.2} />
      Approvers only
    </span>
  );
}

function formatDate(iso: string): string {
  // Parse date-only strings by y/m/d components (a local Date) rather than
  // new Date(`${iso}T00:00:00`), whose date-string parsing is inconsistent across
  // browsers. Mirrors ReportingPeriodsPane's parseIso for a consistent app-wide
  // approach.
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function LockedBadge({ date }: { date: string }) {
  return (
    <span className="cc-badge cc-badge--locked">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
        <path d="M9 5H8V3.5a2 2 0 0 0-4 0V5H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1zm-3 3.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM7 5H5V3.5a1 1 0 0 1 2 0V5z" />
      </svg>
      Locked · {formatDate(date)}
    </span>
  );
}

// ── Row action menu ──────────────────────────────────────────────────────────

interface MenuAction {
  label: string;
  onSelect: () => void;
  icon: React.ReactNode;
  destructive?: boolean;
  /**
   * Draw a hairline above this item, grouping what follows apart from what came
   * before. Purely visual and `aria-hidden`: this popup is a plain button list,
   * not a `role="menu"`, so there is no `role="separator"` contract to honour
   * and claiming one would imply the menu semantics the list deliberately
   * doesn't offer.
   */
  separatorBefore?: boolean;
  /**
   * Present only when the action cannot be taken, and it carries the reason.
   * One field rather than a `disabled` flag beside an optional `disabledReason`,
   * so a reason can never be attached to an action that is actually available.
   */
  unavailable?: { reason: string };
}

function RowMenu({ label, actions }: { label: string; actions: MenuAction[] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const baseId = useId();

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="cc-menu-wrap" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="cc-kebab"
        // No aria-haspopup: the popup is a Tab-navigated plain button list, not a
        // role="menu", so advertising a menu popup would mislead AT. aria-expanded
        // is the honest disclosure signal.
        aria-expanded={open}
        aria-label={`Actions for ${label}`}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>
      {/* A plain button list (Tab-navigated) rather than role="menu" — we don't
          implement arrow-key roving, so we don't advertise the menu contract. */}
      {open && (
        <div className="cc-menu">
          {actions.map((a, i) => {
            const descId = `${baseId}-${i}`;
            const labelId = `${baseId}-${i}-label`;
            // aria-disabled (not native `disabled`) keeps the item focusable so
            // the reason below it is reachable and announced; the click is
            // guarded instead. aria-labelledby pins the accessible name to the
            // label alone — without it the visible reason would be folded into
            // the computed name *and* read again as the description.
            return (
              <Fragment key={a.label}>
                {a.separatorBefore && <div className="cc-menu-sep" aria-hidden="true" />}
                <button
                  type="button"
                  className={`cc-menu-item${a.destructive ? ' cc-menu-item--destructive' : ''}${
                    a.unavailable ? ' cc-menu-item--disabled' : ''
                  }`}
                  aria-disabled={a.unavailable ? true : undefined}
                  aria-labelledby={labelId}
                  aria-describedby={a.unavailable ? descId : undefined}
                  onClick={() => {
                    if (a.unavailable) return;
                    setOpen(false);
                    a.onSelect();
                    // Return focus to the trigger; actions that open a form/dialog
                    // move focus into it afterward (their focus runs later), so
                    // this only "sticks" for in-place actions like Reactivate.
                    triggerRef.current?.focus();
                  }}
                >
                  <span className="cc-menu-item__icon" aria-hidden="true">
                    {a.icon}
                  </span>
                  <span className="cc-menu-item__body">
                    <span id={labelId}>{a.label}</span>
                    {/* Visible, not sr-only: a disabled control that won't say why
                      is the whole reason this menu was confusing. Rendered inline
                      rather than as a hover tooltip so it survives touch and
                      keyboard use (WCAG 1.4.13). */}
                    {a.unavailable && (
                      <span id={descId} className="cc-menu-item__reason">
                        {a.unavailable.reason}
                      </span>
                    )}
                  </span>
                </button>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

const EditIcon = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" />
  </svg>
);
const LockMenuIcon = (
  <svg width="15" height="15" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <path d="M9 5H8V3.5a2 2 0 0 0-4 0V5H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1zm-3 3.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM7 5H5V3.5a1 1 0 0 1 2 0V5z" />
  </svg>
);
// Same shield as the pill and as the timesheet badge, at the menu's icon size
// and stroke — one glyph means one thing everywhere this feature appears.
const ShieldMenuIcon = <ShieldGlyph size={15} strokeWidth={1.5} />;
const StarIcon = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 1.75l1.6 3.3 3.65.52-2.62 2.56.6 3.62L7 10.06l-3.23 1.7.6-3.62L1.75 5.57l3.65-.52L7 1.75z" />
  </svg>
);
const PowerIcon = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 1.5v5" />
    <path d="M3.5 3.5a5 5 0 1 0 7 0" />
  </svg>
);

// ── Section states (loading / error / empty) ─────────────────────────────────

const LayersIcon = (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
  </svg>
);
const TagIcon = (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.6 13.4 12 22l-9-9V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
    <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

// Loading: a table-shaped card with a bone header + spinner + shimmer rows,
// so the pane doesn't collapse/shift when data arrives.
function LoadingState({ label }: { label: string }) {
  return (
    <div className="cc-table-wrap" aria-busy="true">
      <div className="cc-loading-head">
        <span className="cc-spinner" aria-hidden="true" />
        <span className="sr-only">Loading {label}…</span>
        <span aria-hidden="true">Loading…</span>
      </div>
      {[0, 1, 2].map((r) => (
        <div className="cc-skel-row" key={r}>
          <span className="cc-skeleton" />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="cc-state cc-state--error" role="alert">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      </svg>
      <h3 className="cc-state__title">Couldn&rsquo;t load {label}</h3>
      <p className="cc-state__body">
        Something went wrong reaching the server. Your existing configuration is safe.
      </p>
      <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
  cta,
  onCta,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  cta: string;
  onCta: () => void;
}) {
  return (
    <div className="cc-state cc-state--empty">
      <span className="cc-state__icon" aria-hidden="true">
        {icon}
      </span>
      <h3 className="cc-state__title">{title}</h3>
      <p className="cc-state__body">{body}</p>
      <button type="button" className="btn btn--primary btn--sm" onClick={onCta}>
        {cta}
      </button>
    </div>
  );
}

// ── Lock-at-cutoff dialog (category only) ─────────────────────────────────────

function LockDialog({
  category,
  isPending,
  onClose,
  onSave,
}: {
  category: AdminCategoryItem | null;
  isPending: boolean;
  onClose: () => void;
  onSave: (date: string | null) => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const open = category !== null;
  useFocusTrap(dialogRef, open);
  const [date, setDate] = useState('');

  useEffect(() => {
    if (category) setDate(category.lockedAt ?? '');
  }, [category]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isPending) onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, isPending]);

  if (!category) return null;

  return (
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="projects-dialog"
        style={{ maxWidth: 460 }}
      >
        <h2 id={titleId} className="dialog-title">
          Lock “{category.name}” at cutoff
        </h2>
        <div className="dialog-body">
          <p style={{ color: 'var(--fg2)', fontSize: 'var(--fs-14)', lineHeight: 1.5 }}>
            After the cutoff date, no new hours can be charged to this category. Existing timesheet
            entries are preserved.
          </p>
          <label className="cc-field__label" htmlFor="cc-lock-date" style={{ marginTop: 12 }}>
            Cutoff date
          </label>
          <input
            id="cc-lock-date"
            type="date"
            className="cc-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="dialog-footer" style={{ justifyContent: 'space-between' }}>
          {category.lockedAt ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm cc-danger-text"
              style={{ marginRight: 'auto' }}
              onClick={() => onSave(null)}
              disabled={isPending}
            >
              Remove lock
            </button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => onSave(date || null)}
              disabled={!date || isPending}
              aria-busy={isPending}
            >
              {category.lockedAt ? 'Update lock' : 'Lock at cutoff'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── "Make approvers only" confirmation ───────────────────────────────────────

/**
 * What a `→ approvers only` flip does to the people who hold the category, as
 * TWO sentences rather than one.
 *
 * A single "N contributors affected" would be actively misleading: several of
 * that N keep their row. Somebody who has logged hours — OR left a note on the
 * row without logging any — keeps the line, retained and read-only, because a
 * line with content on it never disappears (U-07). Somebody whose row is empty
 * simply stops having it. Those are different enough outcomes that conflating
 * them would misinform the one person deciding.
 *
 * "hours or a note", not "hours logged", because that is exactly what the server
 * counts: `countApproverOnlyAudienceImpact` sides a user with `bool_or(entry is
 * not null or description is not null)`, the exact negation of what
 * `safelyEmptyLine` deletes on. A note-only holder keeps their row and is
 * counted here as keeping it; describing that number as "with hours logged"
 * quotes a true count under a false label.
 *
 * The flip is immediate, so both sentences are present-tense: the server sweeps
 * on the PATCH and stops seeding from that moment.
 */
function retainedSentence(count: number): string {
  if (count === 0) return 'Nobody has hours or a note against it.';
  return `${count} contributor${count === 1 ? '' : 's'} with hours or a note keep${count === 1 ? 's' : ''} this row, read-only.`;
}

function losingSentence(count: number): string {
  if (count === 0) return 'Nobody is holding an empty row to lose.';
  return `${count} contributor${count === 1 ? '' : 's'} with an empty row lose${count === 1 ? 's' : ''} it now.`;
}

function ApproverOnlyImpactBody({
  name,
  impact,
  isLoading,
  failed,
}: {
  name: string;
  impact: AdminCategoryAudienceImpact | undefined;
  isLoading: boolean;
  failed: boolean;
}) {
  return (
    <>
      <p style={{ margin: '0 0 8px' }}>
        “{name}” is seeded only onto approvers’ and administrators’ timesheets from now on. This
        takes effect immediately, including the week in progress.
      </p>
      {/* The counts land after the dialog opens, which is after aria-describedby
          has already been read. Polite so the numbers are announced when they
          arrive rather than silently replacing "Checking…". */}
      <p style={{ margin: 0 }} aria-live="polite">
        {isLoading
          ? 'Checking who this affects…'
          : failed || !impact
            ? 'Couldn’t check who this affects right now.'
            : `${retainedSentence(impact.retainingContributors)} ${losingSentence(
                impact.losingContributors,
              )}`}
      </p>
    </>
  );
}

// ── Categories section ────────────────────────────────────────────────────────

type CatFormState = { mode: 'add' } | { mode: 'edit'; category: AdminCategoryItem } | null;

/**
 * A category patch held back until the administrator confirms it.
 *
 * Only the `→ approvers only` direction is ever parked here — that is the
 * direction that takes rows off other people's timesheets. The reverse is a
 * plain toggle, as specified: re-seeding a category destroys nothing.
 *
 * Both entry points funnel through this one shape — the row-menu toggle and the
 * edit form's radio — so the warning cannot be reached by one route and skipped
 * by the other.
 */
interface PendingApproverOnly {
  category: AdminCategoryItem;
  /** The name to quote — the edited one when the change comes from the form. */
  name: string;
  /**
   * EXACTLY the keys to send. Never a whole round-tripped object: this PATCH
   * has no version or If-Match, so a key included "for completeness" carries
   * whatever value the pane last saw and silently reverts a colleague's change.
   * The row-menu toggle therefore sends `{ approverOnly }` alone, and the form
   * sends only the fields the administrator actually edited.
   */
  patch: UpdateAdminCategoryRequest;
  /** Close the inline form once the patch lands (the form path only). */
  fromForm: boolean;
}

function CategoriesSection({ pushToast }: { pushToast: (m: string, k: ToastKind) => void }) {
  const query = useAdminCategories();
  const createM = useCreateCategory();
  const updateM = useUpdateCategory();

  const [form, setForm] = useState<CatFormState>(null);
  const [name, setName] = useState('');
  const [isWorkTime, setIsWorkTime] = useState(true);
  const [approverOnly, setApproverOnly] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<AdminCategoryItem | null>(null);
  const [pendingApproverOnly, setPendingApproverOnly] = useState<PendingApproverOnly | null>(null);
  const [lockFor, setLockFor] = useState<AdminCategoryItem | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const radioGroupId = useId();
  const visibilityGroupId = useId();
  const visibilityHintId = useId();

  // Fetched only while a confirmation is pending, and never cached across
  // opens — see useCategoryAudienceImpact.
  const impact = useCategoryAudienceImpact(pendingApproverOnly?.category.categoryId ?? null);

  function openAdd() {
    setName('');
    setIsWorkTime(true);
    setApproverOnly(false);
    setForm({ mode: 'add' });
    setTimeout(() => nameRef.current?.focus(), 0);
  }
  function openEdit(category: AdminCategoryItem) {
    setName(category.name);
    setIsWorkTime(category.isWorkTime);
    setApproverOnly(category.approverOnly);
    setForm({ mode: 'edit', category });
    setTimeout(() => nameRef.current?.focus(), 0);
  }
  function closeForm() {
    setForm(null);
  }

  /**
   * The one place a category patch is sent.
   *
   * The success branch reads the RETURNED ROW, not the status code. Zod objects
   * are non-strict, so an API older than this feature parses the patch with a
   * schema that has no `approverOnly` key and SILENTLY STRIPS it: the request
   * still answers 200, the row comes back unchanged, and announcing success
   * there would tell the administrator that contributors had stopped being
   * seeded a category they are still being seeded. Deploy skew makes that
   * reachable in production, not just against a stale sandbox — CloudFront can
   * serve this bundle while a warm Lambda is still on the previous version.
   * `setDefault` in the classifications section below does the same thing for
   * the same reason.
   */
  function applyCategoryPatch({ category, name: quoted, patch, fromForm }: PendingApproverOnly) {
    updateM.mutate(
      { categoryId: category.categoryId, patch },
      {
        onSuccess: (updated) => {
          const intended = patch.approverOnly;
          if (intended !== undefined && updated.approverOnly !== intended) {
            pushToast(
              `Couldn’t change who “${quoted}” is seeded onto — the server didn’t apply the change. It may be running an older version.`,
              'error',
            );
          } else if (intended !== undefined) {
            pushToast(
              intended
                ? `“${quoted}” is now approvers only — it’s seeded onto approvers’ and administrators’ timesheets only.`
                : `“${quoted}” is now visible to everyone.`,
              'success',
            );
          } else {
            pushToast('Category updated.', 'success');
          }
          if (fromForm) closeForm();
        },
        onError: (err) => pushToast(err.message, 'error'),
      },
    );
  }

  /**
   * Every category patch goes through here, and exactly one shape of patch is
   * held back: the `→ approvers only` direction, which takes rows off other
   * people's timesheets. Everything else — the reverse flip, a rename, a
   * work-type change — goes straight through, because none of them removes
   * anything. Gating on the DIRECTION rather than on the control means the edit
   * form cannot become a way around the warning the row menu shows.
   */
  function sendCategoryPatch(pending: PendingApproverOnly) {
    if (pending.patch.approverOnly === true) setPendingApproverOnly(pending);
    else applyCategoryPatch(pending);
  }

  function submitForm(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || !form) return;
    if (form.mode === 'add') {
      createM.mutate(
        { name: trimmed, isWorkTime, approverOnly },
        {
          onSuccess: (created) => {
            if (approverOnly && !created.approverOnly) {
              pushToast(
                `Created “${trimmed}”, but not as approvers only — the server didn’t apply that part. It may be running an older version.`,
                'error',
              );
            } else {
              pushToast(
                created.approverOnly
                  ? `Created “${trimmed}” — it appears on approvers’ and administrators’ timesheets only.`
                  : `Created “${trimmed}”.`,
                'success',
              );
            }
            closeForm();
          },
          onError: (err) => pushToast(err.message, 'error'),
        },
      );
    } else {
      // Only what changed. Round-tripping the whole object was survivable while
      // the form held two fields the toggles never touch; with the audience
      // radio on it, an untouched `approverOnly` would ship the value captured
      // when the form opened and quietly revert a colleague's flip.
      const current = form.category;
      const patch: UpdateAdminCategoryRequest = {};
      if (trimmed !== current.name) patch.name = trimmed;
      if (isWorkTime !== current.isWorkTime) patch.isWorkTime = isWorkTime;
      if (approverOnly !== current.approverOnly) patch.approverOnly = approverOnly;
      sendCategoryPatch({ category: current, name: trimmed, patch, fromForm: true });
    }
  }

  function setActive(category: AdminCategoryItem, active: boolean) {
    updateM.mutate(
      { categoryId: category.categoryId, patch: { activeFlag: active } },
      {
        onSuccess: () =>
          pushToast(active ? 'Category reactivated.' : 'Category deactivated.', 'success'),
        onError: (err) => pushToast(err.message, 'error'),
      },
    );
  }

  const categories = query.data ?? [];
  const busy = createM.isPending || updateM.isPending;

  return (
    <section className="cc-section" aria-labelledby="cc-cat-title">
      <div className="cc-section__head">
        <div>
          <h2 id="cc-cat-title" className="cc-section__title">
            Non-project categories
          </h2>
          {/* Diffed against the live copy above, not the prototype's, whose
              pane was rebuilt natively and whose baseline has drifted. One
              clause added, and it says "seeded onto" rather than "hidden from":
              the catalog is ungated, so this is a routing rule, not a privacy
              one. */}
          <p className="cc-section__desc">
            Standing non-project time buckets like Gen Admin and Leave. Admins manage them here —
            contributors don&rsquo;t create their own. Approver-only categories are seeded only onto
            approvers&rsquo; and administrators&rsquo; timesheets.
          </p>
        </div>
        {!form && !query.isLoading && !query.error && categories.length > 0 && (
          <button type="button" className="btn btn--primary btn--sm" onClick={openAdd}>
            + New category
          </button>
        )}
      </div>

      {form && (
        <form
          className="cc-form"
          onSubmit={submitForm}
          aria-label={form.mode === 'add' ? 'New category' : 'Edit category'}
        >
          <div className="cc-field cc-field--grow">
            <label className="cc-field__label" htmlFor="cc-cat-name">
              Name
            </label>
            <input
              id="cc-cat-name"
              ref={nameRef}
              type="text"
              className="cc-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jury Duty"
              maxLength={255}
              aria-required="true"
            />
          </div>
          <div className="cc-field">
            <span className="cc-field__label" id={radioGroupId}>
              Type
            </span>
            <div className="cc-radio" role="radiogroup" aria-labelledby={radioGroupId}>
              {[
                { v: true, label: categoryTypeLabel(true) },
                { v: false, label: categoryTypeLabel(false) },
              ].map((opt) => (
                <label
                  key={opt.label}
                  className={`cc-radio__opt${isWorkTime === opt.v ? ' selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="cc-cat-type"
                    checked={isWorkTime === opt.v}
                    onChange={() => setIsWorkTime(opt.v)}
                  />
                  <Dot filled={opt.v} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          {/* Mirrors the Type group exactly — native radios inside a labelled
              role="radiogroup", not the handoff's role="radio" buttons. Real
              inputs get arrow-key roving, Space activation and the checked
              state for free, and the accessibility tree reports the same
              "checked" either way. */}
          <div className="cc-field">
            <span className="cc-field__label" id={visibilityGroupId}>
              Visible to
            </span>
            <div
              className="cc-radio"
              role="radiogroup"
              aria-labelledby={visibilityGroupId}
              aria-describedby={visibilityHintId}
            >
              {[
                { v: false, label: 'Everyone', glyph: <GlobeGlyph size={13} /> },
                { v: true, label: 'Approvers only', glyph: <ShieldGlyph size={13} /> },
              ].map((opt) => (
                <label
                  key={opt.label}
                  className={`cc-radio__opt${approverOnly === opt.v ? ' selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="cc-cat-visibility"
                    checked={approverOnly === opt.v}
                    onChange={() => setApproverOnly(opt.v)}
                  />
                  {opt.glyph}
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div className="cc-form__actions">
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={closeForm}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              disabled={!name.trim() || busy}
              aria-busy={busy}
            >
              {form.mode === 'add' ? 'Add category' : 'Save changes'}
            </button>
          </div>
          {/* The second clause is not decoration. Without it an administrator
              reads "approvers only" as a confidentiality control and sooner or
              later names a category something sensitive — and the catalog is
              ungated, so contributors can still read the name, and the hours
              still reach shared analytics. The person making that decision is
              otherwise the one person nobody tells. */}
          <p className="cc-form__hint" id={visibilityHintId}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <span>
              {approverOnly
                ? 'Seeded only onto approvers’ and administrators’ timesheets. It controls which timesheets the category appears on — not who can see its name or its hours — so don’t use it for confidential work.'
                : 'Appears on everyone’s timesheet, ready to fill in.'}
            </span>
          </p>
        </form>
      )}

      {query.isLoading ? (
        <LoadingState label="categories" />
      ) : query.error ? (
        <ErrorState label="categories" onRetry={() => void query.refetch()} />
      ) : categories.length === 0 && !form ? (
        <EmptyState
          icon={LayersIcon}
          title="No categories yet"
          body="Add the first standing non-project category, like “Gen Admin” or “Leave”."
          cta="+ New category"
          onCta={openAdd}
        />
      ) : (
        <div className="cc-table-wrap">
          <table className="cc-table" aria-label="Non-project categories">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Type</th>
                <th scope="col">Visible to</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.categoryId} className={cat.activeFlag ? '' : 'cc-row--inactive'}>
                  <td className="cc-cell-name">{cat.name}</td>
                  <td>
                    <TypeBadge isWorkTime={cat.isWorkTime} />
                  </td>
                  <td>
                    <VisibleToCell approverOnly={cat.approverOnly} />
                  </td>
                  <td>
                    <span className="cc-status-cell">
                      <StatusBadge active={cat.activeFlag} />
                      {cat.lockedAt && <LockedBadge date={cat.lockedAt} />}
                    </span>
                  </td>
                  <td className="cc-cell-actions">
                    <RowMenu
                      label={cat.name}
                      actions={[
                        { label: 'Edit', icon: EditIcon, onSelect: () => openEdit(cat) },
                        {
                          label: cat.lockedAt ? 'Change / remove lock…' : 'Lock at cutoff…',
                          icon: LockMenuIcon,
                          onSelect: () => setLockFor(cat),
                        },
                        {
                          // Available on inactive rows too. It changes nothing
                          // visible there — an inactive category is seeded to
                          // nobody — but the server accepts it, and blocking it
                          // would invent a rule the API doesn't have while
                          // stopping an administrator from setting the audience
                          // before they reactivate.
                          label: cat.approverOnly ? 'Show to everyone' : 'Make approvers only',
                          icon: ShieldMenuIcon,
                          onSelect: () =>
                            sendCategoryPatch({
                              category: cat,
                              name: cat.name,
                              // The flag alone. Not the row.
                              patch: { approverOnly: !cat.approverOnly },
                              fromForm: false,
                            }),
                        },
                        cat.activeFlag
                          ? {
                              label: 'Deactivate',
                              icon: PowerIcon,
                              destructive: true,
                              separatorBefore: true,
                              onSelect: () => setConfirmDeactivate(cat),
                            }
                          : {
                              label: 'Reactivate',
                              icon: PowerIcon,
                              separatorBefore: true,
                              onSelect: () => setActive(cat, true),
                            },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmDeactivate !== null}
        title="Deactivate category?"
        message={
          confirmDeactivate ? (
            <>
              Deactivate <strong>{confirmDeactivate.name}</strong>? It will stop appearing on new
              timesheets. Existing timesheet entries are preserved for reporting — nothing is
              deleted.
            </>
          ) : null
        }
        confirmLabel="Deactivate"
        pendingLabel="Deactivating…"
        isPending={updateM.isPending}
        onConfirm={() => {
          if (confirmDeactivate) {
            setActive(confirmDeactivate, false);
            setConfirmDeactivate(null);
          }
        }}
        onClose={() => setConfirmDeactivate(null)}
      />

      {/* Fires at every count, zero included, so the interaction is
          predictable: an administrator who has seen it once knows this is the
          direction that takes rows away, whether or not anyone holds one
          today. The reverse direction has no dialog — re-seeding destroys
          nothing. */}
      <ConfirmDialog
        open={pendingApproverOnly !== null}
        title={pendingApproverOnly ? `Make “${pendingApproverOnly.name}” approvers only?` : ''}
        message={
          pendingApproverOnly ? (
            <ApproverOnlyImpactBody
              name={pendingApproverOnly.name}
              impact={impact.data}
              isLoading={impact.isPending}
              failed={impact.error !== null}
            />
          ) : null
        }
        confirmLabel="Make approvers only"
        pendingLabel="Saving…"
        isPending={updateM.isPending}
        /* Inert until the counts land. The confirm button autofocuses on open,
           so without this a fast Enter confirms while the body still reads
           "Checking who this affects…" — consent to a blast radius the admin
           never saw, which is the one thing this dialog exists to prevent.
           Not disabled when the count FAILS: a lookup that errored should not
           permanently block a legitimate admin action, and the body says so. */
        confirmDisabled={impact.isPending}
        onConfirm={() => {
          if (pendingApproverOnly) {
            applyCategoryPatch(pendingApproverOnly);
            setPendingApproverOnly(null);
          }
        }}
        onClose={() => setPendingApproverOnly(null)}
      />

      <LockDialog
        category={lockFor}
        isPending={updateM.isPending}
        onClose={() => setLockFor(null)}
        onSave={(date) => {
          if (!lockFor) return;
          updateM.mutate(
            { categoryId: lockFor.categoryId, patch: { lockedAt: date } },
            {
              onSuccess: () => {
                pushToast(date ? 'Category locked.' : 'Lock removed.', 'success');
                setLockFor(null);
              },
              onError: (err) => pushToast(err.message, 'error'),
            },
          );
        }}
      />
    </section>
  );
}

// ── Classifications section ────────────────────────────────────────────────────

type ClsFormState = { mode: 'add' } | { mode: 'edit'; cls: LineClassificationDto } | null;

function ClassificationsSection({ pushToast }: { pushToast: (m: string, k: ToastKind) => void }) {
  const query = useClassifications();
  const createM = useCreateClassification();
  const updateM = useUpdateClassification();

  const [form, setForm] = useState<ClsFormState>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState<LineClassificationDto | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  function openAdd() {
    setName('');
    setDescription('');
    setForm({ mode: 'add' });
    setTimeout(() => nameRef.current?.focus(), 0);
  }
  function openEdit(cls: LineClassificationDto) {
    setName(cls.name);
    setDescription(cls.description ?? '');
    setForm({ mode: 'edit', cls });
    setTimeout(() => nameRef.current?.focus(), 0);
  }
  function closeForm() {
    setForm(null);
  }

  function submitForm(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || !form) return;
    const desc = description.trim();
    if (form.mode === 'add') {
      createM.mutate(
        { name: trimmed, description: desc || undefined },
        {
          onSuccess: () => {
            pushToast(`Created “${trimmed}”.`, 'success');
            closeForm();
          },
          onError: (err) => pushToast(err.message, 'error'),
        },
      );
    } else {
      updateM.mutate(
        {
          classificationId: form.cls.classificationId,
          patch: { name: trimmed, description: desc || null },
        },
        {
          onSuccess: () => {
            pushToast('Classification updated.', 'success');
            closeForm();
          },
          onError: (err) => pushToast(err.message, 'error'),
        },
      );
    }
  }

  function setDefault(cls: LineClassificationDto) {
    updateM.mutate(
      { classificationId: cls.classificationId, patch: { isDefault: true } },
      {
        // A 200 is not proof the flag moved, so the response body decides the
        // message rather than the status code.
        //
        // An API older than this feature parses the patch with a non-strict Zod
        // object that has no `isDefault` key, which SILENTLY STRIPS it: the patch
        // reduces to `{}`, the row comes back untouched, and the request still
        // succeeds. Announcing success there tells the admin the default moved
        // when it did not — the one failure this pane must not report as a win.
        // Deploy skew makes that reachable in production too, not just against a
        // stale sandbox: CloudFront can serve this bundle while a warm Lambda is
        // still running the previous version.
        onSuccess: (updated) => {
          if (updated.isDefault) {
            pushToast(`“${cls.name}” is now the default for new tasks.`, 'success');
          } else {
            pushToast(
              `Couldn’t make “${cls.name}” the default — the server didn’t apply the change. It may be running an older version.`,
              'error',
            );
          }
        },
        onError: (err) => pushToast(err.message, 'error'),
      },
    );
  }

  function setActive(cls: LineClassificationDto, active: boolean) {
    updateM.mutate(
      { classificationId: cls.classificationId, patch: { activeFlag: active } },
      {
        onSuccess: () =>
          pushToast(
            active ? 'Classification reactivated.' : 'Classification deactivated.',
            'success',
          ),
        onError: (err) => pushToast(err.message, 'error'),
      },
    );
  }

  const rows = query.data ?? [];
  const busy = createM.isPending || updateM.isPending;
  // Every task points at a classification, so the catalog can never be emptied.
  // The server enforces this too (409); disabling here explains it up front.
  const activeCount = rows.filter((c) => c.activeFlag).length;
  const LAST_ACTIVE_REASON = 'The last active classification can’t be deactivated.';
  // Each of these mirrors a 409 the server returns. Disabling the item up front
  // saves a round trip and, more to the point, names the remedy before the click
  // rather than after it. The server stays the enforcement; this is the courtesy.
  const DEFAULT_LOCKED_REASON = 'Make another classification the default first.';
  const ALREADY_DEFAULT_REASON = 'This is already the default for new tasks.';
  const INACTIVE_DEFAULT_REASON = 'Reactivate this classification first.';

  return (
    <section className="cc-section" aria-labelledby="cc-cls-title">
      <div className="cc-section__head">
        <div>
          <h2 id="cc-cls-title" className="cc-section__title">
            Line classifications
          </h2>
          <p className="cc-section__desc">
            Tags that split a project line by activity type, so one task can roll up into several
            reporting buckets. This is their org-wide home. The one marked Default is preselected on
            every new task.
          </p>
        </div>
        {!form && !query.isLoading && !query.error && rows.length > 0 && (
          <button type="button" className="btn btn--primary btn--sm" onClick={openAdd}>
            + New classification
          </button>
        )}
      </div>

      {form && (
        <form
          className="cc-form"
          onSubmit={submitForm}
          aria-label={form.mode === 'add' ? 'New classification' : 'Edit classification'}
        >
          <div className="cc-field">
            <label className="cc-field__label" htmlFor="cc-cls-name">
              Name
            </label>
            <input
              id="cc-cls-name"
              ref={nameRef}
              type="text"
              className="cc-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Travel"
              maxLength={255}
              aria-required="true"
            />
          </div>
          <div className="cc-field cc-field--grow">
            <label className="cc-field__label" htmlFor="cc-cls-desc">
              Description
            </label>
            <input
              id="cc-cls-desc"
              type="text"
              className="cc-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description"
              maxLength={1000}
            />
          </div>
          <div className="cc-form__actions">
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={closeForm}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              disabled={!name.trim() || busy}
              aria-busy={busy}
            >
              {form.mode === 'add' ? 'Add classification' : 'Save changes'}
            </button>
          </div>
        </form>
      )}

      {query.isLoading ? (
        <LoadingState label="classifications" />
      ) : query.error ? (
        <ErrorState label="classifications" onRetry={() => void query.refetch()} />
      ) : rows.length === 0 && !form ? (
        <EmptyState
          icon={TagIcon}
          title="No line classifications yet"
          body="Add the first classification — “Direct”, “Travel”, “Training” — to let project lines be split by activity type."
          cta="+ New classification"
          onCta={openAdd}
        />
      ) : (
        <div className="cc-table-wrap">
          <table className="cc-table" aria-label="Line classifications">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Description</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((cls) => {
                const isLastActive = cls.activeFlag && activeCount === 1;
                return (
                  <tr
                    key={cls.classificationId}
                    className={cls.activeFlag ? '' : 'cc-row--inactive'}
                  >
                    <td className="cc-cell-name">{cls.name}</td>
                    <td className="cc-cell-desc">
                      {cls.description ? cls.description : <span className="cc-emdash">—</span>}
                    </td>
                    <td>
                      <div className="cc-badge-stack">
                        <StatusBadge active={cls.activeFlag} />
                        {cls.isDefault && (
                          <>
                            {/* Two adjacent element nodes with no text between
                                them can flatten to "ActiveDefault" in the
                                accessibility tree. The hidden comma makes the
                                separation deliberate rather than up to the
                                browser's text-flattening rules. */}
                            <span className="sr-only">, </span>
                            <DefaultBadge />
                          </>
                        )}
                      </div>
                    </td>
                    <td className="cc-cell-actions">
                      <RowMenu
                        label={cls.name}
                        actions={[
                          {
                            label: 'Edit',
                            icon: EditIcon,
                            onSelect: () => openEdit(cls),
                          },
                          {
                            label: 'Set as default',
                            icon: StarIcon,
                            ...(cls.isDefault
                              ? { unavailable: { reason: ALREADY_DEFAULT_REASON } }
                              : !cls.activeFlag
                                ? { unavailable: { reason: INACTIVE_DEFAULT_REASON } }
                                : {}),
                            onSelect: () => setDefault(cls),
                          },
                          cls.activeFlag
                            ? {
                                label: 'Deactivate',
                                icon: PowerIcon,
                                destructive: true,
                                // Last-active first: it is the more fundamental
                                // invariant, and it is the reason the server
                                // reports first too.
                                ...(isLastActive
                                  ? { unavailable: { reason: LAST_ACTIVE_REASON } }
                                  : cls.isDefault
                                    ? { unavailable: { reason: DEFAULT_LOCKED_REASON } }
                                    : {}),
                                onSelect: () => setConfirmDeactivate(cls),
                              }
                            : {
                                label: 'Reactivate',
                                icon: PowerIcon,
                                onSelect: () => setActive(cls, true),
                              },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmDeactivate !== null}
        title="Deactivate classification?"
        message={
          confirmDeactivate ? (
            <>
              Deactivate <strong>{confirmDeactivate.name}</strong>? New project lines won&rsquo;t
              offer this classification. Existing lines and their history are preserved — inactive
              classifications are kept for reporting, never deleted.
            </>
          ) : null
        }
        confirmLabel="Deactivate"
        pendingLabel="Deactivating…"
        isPending={updateM.isPending}
        onConfirm={() => {
          if (confirmDeactivate) {
            setActive(confirmDeactivate, false);
            setConfirmDeactivate(null);
          }
        }}
        onClose={() => setConfirmDeactivate(null)}
      />
    </section>
  );
}

// ── Pane ───────────────────────────────────────────────────────────────────────

export function CategoriesPane() {
  const { toasts, pushToast, dismiss } = useToasts();

  return (
    <div className="cc-pane">
      <CategoriesSection pushToast={pushToast} />
      <hr className="cc-divider" />
      <ClassificationsSection pushToast={pushToast} />
      <p className="cc-foot">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <path d="M8 7v4M8 5.2a.6.6 0 1 1 0-1.2.6.6 0 0 1 0 1.2z" />
        </svg>
        <span>
          <strong>Deactivate, never delete.</strong> Anything that&rsquo;s been logged against stays
          for historical reporting. Locking a category stops new hours after a cutoff while keeping
          what&rsquo;s already there. <strong>Approvers only</strong> seeds a category onto
          approvers&rsquo; and administrators&rsquo; timesheets alone, and their hours roll up the
          same way — it controls which timesheets a category is seeded onto, not who can see its
          name or its hours, so don&rsquo;t use it for confidential work.
        </span>
      </p>
      <ToastRegion toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
